import { createSelector } from 'reselect';

import { TProductCategory } from '@lib/core/products/types';
import { AppStateType } from '@lib/core/service/types/appStateType';
import { ThirdPartyConnectModalShown } from '@lib/core/service/types/interface';
import { languages } from '@lib/tools/locale/utils/consts';
import { RouteUtils } from '@lib/tools/routes';

export const selectServiceState = (state: AppStateType) => state.service;

/**
 * @returns The value of the isDevToolsEnabled property or the defaultValue.
 */
export const selectIsDevToolsEnabled = (state: AppStateType, defaultValue = false): boolean =>
  state.service.isDevToolsEnabled || defaultValue;

/**
 * @returns a boolean value to mock the hosting site and iframe behavior with a mock 1080x1920 wrapper
 */
export const selectIsHostMocked = (state: AppStateType, defaultValue = false): boolean =>
  state.service.isHostMocked || defaultValue;

/**
 * @returns the edit mode for translations. Enables additional behavior on `LocaleFragment`
 */
export const selectIsLocaleEditMode = (state: AppStateType): boolean => state.service.isLocaleEditMode;

/**
 * @returns the first product category of the retailer. Used as default.
 */
export const selectServiceProductCategory = (state: AppStateType): TProductCategory =>
  state?.service?.productCategory || RouteUtils.getProductCategory();
/**
 * @returns A boolean indicating whether the taste path start page should be displayed.
 */
export const selectShouldShowTastePathStartPage = createSelector(
  [selectServiceState],
  (state): boolean => state.shouldShowTastePathStartPage,
);

export const selectIsTastePathOnboardingShown = createSelector(
  [selectServiceState],
  state => state.isTastePathOnboardingShown,
);

export const selectIsFooterTutorialDisplayed = createSelector(
  [selectServiceState],
  state => state.isFooterTutorialDisplayed,
);

export const selectIsFunctionalityExplanationDisplayed = createSelector(
  [selectServiceState],
  state => state.isFunctionalityExplanationDisplayed,
);

export const selectAppRoute = (state: AppStateType, defaultValue = ''): string => state?.service?.route || defaultValue;

export const selectAppPreviousRoute = (state: AppStateType, defaultValue = ''): string =>
  state?.service?.previousRoute || defaultValue;

/**
 * @returns the current language of the application
 */
export const selectLocale = (state: AppStateType, defaultValue = languages.ENGLISH): string =>
  state?.service?.lang || defaultValue;

/**
 * @returns A boolean indicating whether b2c app SplashLoading screen is shown.
 */

export const selectIsSplashScreenPageShown = (state: AppStateType, defaultValue = false): boolean =>
  state?.service?.isSplashScreenPageShown || defaultValue;

/**
 * @returns A boolean indicating whether Guide to Taste Id Page on Widget is shown.
 */

export const selectIsTasteIdGuideShown = (state: AppStateType, defaultValue = false): boolean =>
  state?.service?.isTasteIdGuideShown || defaultValue;

export const selectIsSelectProductTooltipShown = (state: AppStateType): boolean =>
  state?.service?.isSelectProductTooltipShown;

/**
 * @returns A boolean indicating whether Download App Card is to be hidden.
 */

export const selectShouldHideDownloadAppCard = (state: AppStateType, defaultValue = false): boolean =>
  state?.service?.shouldHideDownloadAppCard || defaultValue;

/**
 * @returns A boolean indicating whether third party connect modal is shown
 */
export const selectIsThirdPartyConnectModalShown = (
  state: AppStateType,
  defaultValue = {
    location: false,
    rate4: false,
    rate5: false,
    wishlist: false,
  },
): ThirdPartyConnectModalShown => state?.service?.isThirdPartyConnectModalShown || defaultValue;

/**
 * @returns Case which triggered the Third Party Connect Modal
 */
export const selectThirdPartyConnectModalScenario = createSelector(
  [selectServiceState],
  state => state.thirdPartyConnectModalOpenScenario,
);

/**
 * @returns A string for fidelityId received when being redirected to mobile kiosk.
 */
export const selectFidelityCardIdFromKiosk = (state: AppStateType, defaultValue = ''): string =>
  state?.service?.fidelityCardIdFromKiosk || defaultValue;

export const selectShouldShowScanBarcode = createSelector([selectServiceState], state => state.shouldShowScanBarcode);

export const selectIsTestResultView = createSelector([selectServiceState], state => state.isTestResultView);
